import {
  defaultTurnstileOpenRelayNumber,
  normalizeWorkspaceDevices,
  normalizeWorkspacesForUi,
} from "app/pages/customers/dsok/utils/normalizeDsokWorkspaces";

function trimTurnstileText(value) {
  if (value == null) return "";
  return String(value).trim();
}

function isValidIpv4(value) {
  const parts = trimTurnstileText(value).split(".");
  if (parts.length !== 4) return false;
  return parts.every((part) => {
    if (!/^\d+$/.test(part)) return false;
    if (part.length > 1 && part.startsWith("0")) return false;
    const number = Number(part);
    return Number.isInteger(number) && number >= 0 && number <= 255;
  });
}

/** Matches {@link DsokWorkspaceDevicesEditor} device tab id resolution. */
function workspaceDeviceRowId(device, workspaceIndex, deviceIndex) {
  const d = device ?? {};
  return d?.uuid != null && String(d.uuid).trim() !== ""
    ? String(d.uuid).trim()
    : `device-tab-${workspaceIndex}-${deviceIndex}`;
}

function sanitizeTurnstileDeviceForApiPayload(device) {
  if (!device || typeof device !== "object") return device;
  if (String(device.type ?? "").trim() !== "turnstile") return device;
  const ad = device.additionalData;
  if (!ad || typeof ad !== "object") return device;
  const next = { ...ad };
  if ("turnstileNumber" in next) {
    delete next.turnstileNumber;
  }
  next.qrCodeIpIn = trimTurnstileText(next.qrCodeIpIn);
  next.qrCodeIpOut = trimTurnstileText(next.qrCodeIpOut);
  next.openRelayNumberIn = defaultTurnstileOpenRelayNumber(next.openRelayNumberIn);
  next.openRelayNumberOut = defaultTurnstileOpenRelayNumber(
    next.openRelayNumberOut,
  );
  next.disableRelayNumberIn = defaultTurnstileOpenRelayNumber(
    next.disableRelayNumberIn,
  );
  next.disableRelayNumberOut = defaultTurnstileOpenRelayNumber(
    next.disableRelayNumberOut,
  );
  return { ...device, additionalData: next };
}

/**
 * Validates and builds the workspace document payload for save flows (e.g. POS workspace detail).
 * @param {object} customerWorkspacesSlice - `values.customer.workspaces`
 * @param {function} t - i18n t
 * @param {function} setErrors - Formik setErrors
 * @param {function} setTouched - Formik setTouched
 * @param {{ onlyWorkspaceDataIndex?: number, onlyDeviceUuid?: string } | undefined} options - If `onlyWorkspaceDataIndex` is set, only that workspace row is validated (e.g. POS workspace detail save). If `onlyDeviceUuid` is set (with `onlyWorkspaceDataIndex`), only that device row in that workspace is validated — same id scheme as device tabs in the editor.
 * @returns {{ data: object[] } | null}
 */
export function buildDsokWorkspacesApiPayloadFromCustomerWorkspaces(
  customerWorkspacesSlice,
  t,
  setErrors,
  setTouched,
  options,
) {
  const base = normalizeWorkspacesForUi(customerWorkspacesSlice ?? {});

  const normalizedData = base.data.map((ws) => {
    const features = ws?.features;
    if (!features || typeof features !== "object") return ws;
    if (!("turnstilesEnabled" in features)) return ws;
    const { turnstilesEnabled: _removed, ...restFeatures } = features;
    return { ...ws, features: restFeatures };
  });

  const dataErrors = [];
  const dataTouched = [];
  let hasErrors = false;

  const onlyIdx = options?.onlyWorkspaceDataIndex;
  const onlyDeviceUuid =
    options?.onlyDeviceUuid != null
      ? String(options.onlyDeviceUuid).trim()
      : "";

  normalizedData.forEach((ws, index) => {
    if (
      onlyIdx != null &&
      onlyIdx >= 0 &&
      index !== onlyIdx
    ) {
      return;
    }

    const name = typeof ws?.name === "string" ? ws.name.trim() : "";

    const wsErrors = {};
    const wsTouched = {};
    let wsHasErrors = false;

    if (!name) {
      wsHasErrors = true;
      wsErrors.name = t("messages.checkRequiredFields");
      wsTouched.name = true;
    }

    const devices = normalizeWorkspaceDevices(ws?.devices);
    const devicesErrors = [];
    const devicesTouched = [];

    devices.forEach((device, deviceIndex) => {
      const dev = device ?? {};

      if (
        onlyIdx != null &&
        onlyIdx >= 0 &&
        onlyDeviceUuid !== "" &&
        workspaceDeviceRowId(dev, index, deviceIndex) !== onlyDeviceUuid
      ) {
        return;
      }

      const devName = typeof dev?.name === "string" ? dev.name.trim() : "";
      const devIp =
        typeof dev?.ipAddress === "string" ? dev.ipAddress.trim() : "";
      const devType = String(dev?.type ?? "ticket");

      const deviceErrors = {};
      const deviceTouched = {};
      let deviceHasErrors = false;

      if (!devName) {
        deviceHasErrors = true;
        deviceErrors.name = t("messages.checkRequiredFields");
        deviceTouched.name = true;
      }

      if (!devIp) {
        deviceHasErrors = true;
        deviceErrors.ipAddress = t("messages.checkRequiredFields");
        deviceTouched.ipAddress = true;
      }

      if (devType === "accessTicket") {
        const model =
          typeof dev?.additionalData?.model === "string"
            ? dev.additionalData.model.trim()
            : "";
        if (!model) {
          deviceHasErrors = true;
          deviceErrors.additionalData = {
            ...(deviceErrors.additionalData ?? {}),
            model: t("messages.checkRequiredFields"),
          };
          deviceTouched.additionalData = {
            ...(deviceTouched.additionalData ?? {}),
            model: true,
          };
        }
      }

      if (devType === "turnstile") {
        const turnstileType = trimTurnstileText(
          dev?.additionalData?.turnstileType,
        );
        const qrCodeIdIn = trimTurnstileText(dev?.additionalData?.qrCodeIdIn);
        const qrCodeIdOut = trimTurnstileText(dev?.additionalData?.qrCodeIdOut);
        const qrCodeIpIn = trimTurnstileText(dev?.additionalData?.qrCodeIpIn);
        const qrCodeIpOut = trimTurnstileText(dev?.additionalData?.qrCodeIpOut);
        // Relay selects may show a default in the UI while Formik still has undefined;
        // defaultTurnstileOpenRelayNumber matches sanitize + persisted shape (1–8).
        const invalidIpMessage = t(
          "dsokCustomerDetail.workspaces.devices.turnstile.invalidIpv4",
          {
            defaultValue: "Zadajte platnú IPv4 adresu.",
          },
        );

        if (!turnstileType) {
          deviceHasErrors = true;
          deviceErrors.additionalData = {
            ...(deviceErrors.additionalData ?? {}),
            turnstileType: t("messages.checkRequiredFields"),
          };
          deviceTouched.additionalData = {
            ...(deviceTouched.additionalData ?? {}),
            turnstileType: true,
          };
        }

        if (!qrCodeIdIn) {
          deviceHasErrors = true;
          deviceErrors.additionalData = {
            ...(deviceErrors.additionalData ?? {}),
            qrCodeIdIn: t("messages.checkRequiredFields"),
          };
          deviceTouched.additionalData = {
            ...(deviceTouched.additionalData ?? {}),
            qrCodeIdIn: true,
          };
        }

        if (!qrCodeIdOut) {
          deviceHasErrors = true;
          deviceErrors.additionalData = {
            ...(deviceErrors.additionalData ?? {}),
            qrCodeIdOut: t("messages.checkRequiredFields"),
          };
          deviceTouched.additionalData = {
            ...(deviceTouched.additionalData ?? {}),
            qrCodeIdOut: true,
          };
        }

        if (qrCodeIpIn && !isValidIpv4(qrCodeIpIn)) {
          deviceHasErrors = true;
          deviceErrors.additionalData = {
            ...(deviceErrors.additionalData ?? {}),
            qrCodeIpIn: invalidIpMessage,
          };
          deviceTouched.additionalData = {
            ...(deviceTouched.additionalData ?? {}),
            qrCodeIpIn: true,
          };
        }

        if (qrCodeIpOut && !isValidIpv4(qrCodeIpOut)) {
          deviceHasErrors = true;
          deviceErrors.additionalData = {
            ...(deviceErrors.additionalData ?? {}),
            qrCodeIpOut: invalidIpMessage,
          };
          deviceTouched.additionalData = {
            ...(deviceTouched.additionalData ?? {}),
            qrCodeIpOut: true,
          };
        }
      }

      if (deviceHasErrors) {
        devicesErrors[deviceIndex] = deviceErrors;
        devicesTouched[deviceIndex] = deviceTouched;
      }
    });

    const anyDeviceErrors =
      Array.isArray(devicesErrors) &&
      devicesErrors.some((e) => e != null && Object.keys(e).length > 0);

    if (wsHasErrors) {
      hasErrors = true;
      wsErrors.devices = anyDeviceErrors ? devicesErrors : wsErrors.devices;
      wsTouched.devices = anyDeviceErrors
        ? devicesTouched
        : wsTouched.devices;
      dataErrors[index] = wsErrors;
      dataTouched[index] = wsTouched;
    } else if (anyDeviceErrors) {
      hasErrors = true;
      wsErrors.devices = devicesErrors;
      wsTouched.devices = devicesTouched;
      dataErrors[index] = wsErrors;
      dataTouched[index] = wsTouched;
    }
  });

  if (hasErrors) {
    setTimeout(() => {
      setTouched({
        customer: {
          workspaces: {
            data: dataTouched,
          },
        },
      });
      setErrors({
        customer: {
          workspaces: {
            data: dataErrors,
          },
        },
      });
    }, 0);
    return null;
  }

  return {
    data: normalizedData.map((ws) => ({
      ...ws,
      devices: normalizeWorkspaceDevices(ws?.devices).map(
        sanitizeTurnstileDeviceForApiPayload,
      ),
    })),
  };
}
